import React from 'react';
import { X } from 'lucide-react';
import { SUPPORTED_CURRENCIES } from '../../constants/currencies';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

const GATEWAYS = [
  { value: 'razorpay', label: 'Razorpay' },
  { value: 'stripe', label: 'Stripe (International)' },
  { value: 'cashfree', label: 'Cashfree' },
  { value: 'payu', label: 'PayU' },
  { value: 'ccavenue', label: 'CCAvenue' },
];

/**
 * Phase 24 — payment collection. Reuses the exact same gateway credentials
 * configured under Settings → Integrations that CRM invoices already use
 * (FormCrmMappingService creates a real CrmInvoice under the hood and runs
 * it through PaymentGatewayService — no separate payment infra for forms).
 * Requires a Contact or Account field to already be mapped in CRM Mapping
 * (a CrmInvoice can't exist without one) — this panel doesn't duplicate
 * that warning, CrmMappingPanel already covers identity mapping.
 */
export default function PaymentPanel({ payment, fields, onChange, onClose }) {
  const current = payment || { enabled: false };
  const set = (patch) => onChange({ ...current, ...patch });
  const numberFields = fields.filter((f) => f.type === 'number');

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[440px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Payment Collection</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        <label className="flex items-center gap-2 text-[13px] font-medium text-[#181818] cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={!!current.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
            className="accent-[#0176D3] w-4 h-4"
          />
          Collect a payment on submission
        </label>

        {current.enabled && (
          <div className="space-y-4">
            <p className="text-[11px] text-[#706E6B]">
              Creates a CRM Invoice for the Contact/Account this submission maps to (in CRM Mapping) and
              generates a payment link using your configured gateway credentials — the same ones invoices
              already use under Settings → Integrations. Requires Contact or Account fields to be mapped;
              a submission with neither won't collect payment.
            </p>

            <div>
              <label className={labelClass}>Gateway</label>
              <select className={inputClass} value={current.gateway || ''} onChange={(e) => set({ gateway: e.target.value })}>
                <option value="">Select a gateway</option>
                {GATEWAYS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>

            <div>
              <label className={labelClass}>Amount Source</label>
              <select
                className={inputClass}
                value={current.amountFieldId || ''}
                onChange={(e) => set({ amountFieldId: e.target.value || undefined })}
              >
                <option value="">Fixed amount</option>
                {numberFields.map((f) => <option key={f.id} value={f.id}>{f.label} (from form field)</option>)}
              </select>
              {!numberFields.length && (
                <p className="text-[11px] text-[#706E6B] mt-1">Add a Number field to the form to charge a visitor-entered amount instead.</p>
              )}
            </div>

            {!current.amountFieldId && (
              <div>
                <label className={labelClass}>Fixed Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={inputClass}
                  value={current.amount ?? ''}
                  onChange={(e) => set({ amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            )}

            <div>
              <label className={labelClass}>Currency</label>
              <select className={inputClass} value={current.currency || 'INR'} onChange={(e) => set({ currency: e.target.value })}>
                {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className={labelClass}>Invoice Description</label>
              <input
                className={inputClass}
                value={current.description || ''}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="e.g. Registration Fee"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
